// A component whose code loads after the page renders (010 revision 13, FR-093; plan R13-05): until its module has
// arrived, a placeholder is drawn; once it has, the component is drawn, and from then on it is drawn at once. Unlike
// `React.lazy` with `Suspense`, nothing suspends, so a commit that also carries other new content (a screen's figures)
// is never held back by React's reveal throttling, and a component whose code was fetched ahead (`preload`) draws in
// the same commit as its data. A failed fetch keeps the placeholder and is tried again on the next mount.
import { useEffect, useState, type ComponentType, type ReactNode } from 'react';

/** A deferred component, with the function that fetches its code ahead of its first drawing. */
export type Deferred<P> = ((props: P) => ReactNode) & { preload(): Promise<unknown> };

/**
 * Makes a deferred component.
 * @param load fetches the module and returns the component
 * @param placeholder draws the placeholder from the component's props
 * @returns the component, with `preload`
 */
export function deferred<P extends object>(
  load: () => Promise<ComponentType<P>>,
  placeholder: (props: P) => ReactNode,
): Deferred<P> {
  let loaded: ComponentType<P> | undefined;
  let loading: Promise<ComponentType<P> | undefined> | undefined;
  const preload = () =>
    (loading ??= load().then(
      (component) => (loaded = component),
      () => {
        loading = undefined;
        return undefined;
      },
    ));
  function DeferredComponent(props: P) {
    const [Component, setComponent] = useState(() => loaded);
    useEffect(() => {
      if (Component) return;
      let live = true;
      void preload().then((c) => {
        if (live && c) setComponent(() => c);
      });
      return () => {
        live = false;
      };
    }, [Component]);
    return Component ? <Component {...props} /> : placeholder(props);
  }
  return Object.assign(DeferredComponent, { preload });
}
